import { Trans } from "@lingui/react/macro";
import type { TemplateId } from "../../../convex/templateTypes";
import { TEMPLATE_CATALOG } from "@/lib/templateCatalog";

interface TemplateGalleryGridProps {
  cardTestIdPrefix?: string;
  loadingTemplateId?: TemplateId | null;
  onTemplateClick: (id: TemplateId) => void;
  selectedTemplateId?: TemplateId | null;
  showSelectedBadge?: boolean;
}

export default function TemplateGalleryGrid({
  cardTestIdPrefix,
  loadingTemplateId = null,
  onTemplateClick,
  selectedTemplateId = null,
  showSelectedBadge = true,
}: TemplateGalleryGridProps) {
  return (
    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4">
      {TEMPLATE_CATALOG.map((template) => {
        const isSelected = selectedTemplateId === template.id;
        const isLoading = loadingTemplateId === template.id;

        return (
          <button
            className={`group cursor-pointer overflow-hidden border text-left transition-all duration-300 ${
              isSelected
                ? "border-[var(--color-accent)] bg-white"
                : "border-[var(--color-border)] bg-white hover:border-[var(--color-border-strong)]"
            }`}
            data-testid={
              cardTestIdPrefix
                ? `${cardTestIdPrefix}-${template.id}`
                : undefined
            }
            key={template.id}
            onClick={() => onTemplateClick(template.id)}
            type="button"
          >
            <div className="relative aspect-[16/11] overflow-hidden bg-[var(--color-page-soft)]">
              <img
                alt={template.label}
                className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.04]"
                loading="lazy"
                src={template.screenshot}
              />
              {isLoading && (
                <div
                  className="absolute inset-0 flex items-center justify-center bg-black/30"
                  data-testid={
                    cardTestIdPrefix
                      ? `${cardTestIdPrefix}-loading-${template.id}`
                      : undefined
                  }
                >
                  <div className="flex items-center gap-3 bg-white/90 px-4 py-3 text-sm text-[var(--color-ink)]">
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-[var(--color-border)] border-t-[var(--color-accent)]" />
                    <Trans>Cargando vista previa</Trans>
                  </div>
                </div>
              )}
              {showSelectedBadge && isSelected && (
                <div className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-[var(--color-accent)] text-white">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full border border-white/20">
                    <svg
                      className="h-5 w-5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2.5}
                      viewBox="0 0 24 24"
                    >
                      <path
                        d="M5 13l4 4L19 7"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                </div>
              )}
            </div>
            <div className="px-5 py-4">
              <h3 className="font-display text-2xl italic leading-none text-[var(--color-ink)]">
                {template.label}
              </h3>
            </div>
          </button>
        );
      })}
    </div>
  );
}
